import { Tooltip, Box, Checkbox, Chip } from '@mui/material';
import {
  falsyCheckDate,
  renderChips,
  checkIsSandboxDestroyable,
  checkIsSandboxRemoved,
} from 'utils';
import { Realm, TableConfig } from 'types';
import { Dispatch, SetStateAction, useState } from 'react';
import { ArrayItemRendererWithCutOff } from 'components/ArrayItemRendererWithCutOff';
import { Link } from 'react-router-dom';
import { getTeamsV2PageUrl } from 'utils/url';
import Copy from 'components/Copy';
import { SandboxStatusTypes } from 'SandboxModule/constants';
import type { ChipProps } from '@mui/material';

export const REMOVED_REALM_TOOLTIP = 'Realm is fully removed — nothing left to destroy';

export type RealmListItem = Realm & {
  id: string;
  selected: boolean;
};

export type SandboxTableConfig = TableConfig<
  {
    shouldHideBulkActionSelector?: boolean;
    formatData?: ({ data }: { data: RealmListItem[] }) => RealmListItem[];
  },
  FormattedRowData
>;

// Consumers listing these cells must follow the column order convention in
// docs/patterns.md#column-order-convention (identity → status → creator →
// dates → archive columns → tags → extras).
export enum SandboxTableHeadCellIds {
  Checkbox = 'checkbox',
  ID = 'id',
  RealmId = 'realm_id',
  Name = 'name',
  Created = 'created_at',
  IsReady = 'is_ready',
  URL = 'url',
  Tags = 'tags',
  Status = 'status',
  ArchivedAt = 'archived_at',
  TeamName = 'team_name',
  Creator = 'created_by',
  Archiver = 'archived_by',
  Services = 'realm_services',
  Tenant = 'tenant',
  //   API_SERVER_URL="",
}

type FormattedRowData = Realm & {
  checkedSandboxes: Set<string>;
  navigateToTeamProfile?: (teamId: string | number) => () => void; // Probably not what we want
  setCheckedSandboxesSet: Dispatch<SetStateAction<Set<string>>>;
};

// Badge colors follow the Teams status badge treatment (TeamsV2StatusCell)
const realmStatusColors: Record<string, ChipProps['color']> = {
  [SandboxStatusTypes.Initialized]: 'success',
  [SandboxStatusTypes.Creating]: 'warning',
  [SandboxStatusTypes.NotInitialized]: 'warning',
  [SandboxStatusTypes.PartiallyInitialized]: 'warning',
  [SandboxStatusTypes.Failed]: 'error',
  [SandboxStatusTypes.ReleaseTaskFailed]: 'error',
  [SandboxStatusTypes.InitializationFailed]: 'error',
  [SandboxStatusTypes.Archived]: 'default',
  [SandboxStatusTypes.PartiallyRemoved]: 'default',
  [SandboxStatusTypes.Removed]: 'default',
};

const RealmIdLinkWithCopy = ({
  realm_id,
  tenant,
  is_primary,
}: Pick<FormattedRowData, 'realm_id' | 'tenant' | 'is_primary'>) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Link
        to={getTeamsV2PageUrl({ tenant, realmId: realm_id })}
        style={{ color: 'inherit', textDecoration: 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          component="span"
          sx={{
            mr: 1,
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {realm_id}
        </Box>
      </Link>
      {is_primary && (
        <Tooltip title={`Primary realm for ${tenant} — do not share`}>
          <Chip
            label="Primary"
            size="small"
            variant="outlined"
            color="warning"
            sx={{ mr: 1, height: 20, fontSize: '0.7rem', fontWeight: 500 }}
          />
        </Tooltip>
      )}
      <Box
        sx={{
          visibility: hovered ? 'visible' : 'hidden',
        }}
      >
        <Tooltip title="Copy realm ID">
          <span>
            <Copy textToCopy={realm_id} size="small" />
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const cellRenderer = {
  [SandboxTableHeadCellIds.Status]: ({ status }: FormattedRowData) => {
    if (!status) return <div />;
    return (
      <Chip
        label={status}
        size="small"
        color={realmStatusColors[status] || 'default'}
        sx={{ fontWeight: 500 }}
      />
    );
  },
  [SandboxTableHeadCellIds.Checkbox]: ({
    navigateToTeamProfile,
    checkedSandboxes,
    setCheckedSandboxesSet,
    realm_id,
    is_ready,
    status,
    created_at,
  }: FormattedRowData) => {
    const isSelectable = checkIsSandboxDestroyable({
      status,
      created_at,
    });
    // Two distinct reasons a row can't be selected: teardown already finished
    // (Archived tab), or provisioning is still in flight (Active tab).
    const disabledReason = checkIsSandboxRemoved(status)
      ? REMOVED_REALM_TOOLTIP
      : 'Unable to select sandbox that is in creation';

    return (
      <Tooltip title={isSelectable ? '' : disabledReason}>
        <div>
          <Checkbox
            disabled={!isSelectable}
            checked={checkedSandboxes.has(realm_id)}
            onClick={(e) => {
              e.preventDefault();
              setCheckedSandboxesSet((prev) => {
                const isChecked = prev.has(realm_id);
                if (isChecked) {
                  prev.delete(realm_id);
                } else {
                  prev.add(realm_id);
                }

                return new Set(prev);
              });
            }}
          />
        </div>
      </Tooltip>
    );
  },
  [SandboxTableHeadCellIds.RealmId]: ({
    navigateToTeamProfile,
    realm_id,
    tenant,
    is_primary,
  }: FormattedRowData) => (
    <RealmIdLinkWithCopy realm_id={realm_id} tenant={tenant} is_primary={is_primary} />
  ),
  [SandboxTableHeadCellIds.Created]: ({ navigateToTeamProfile, created_at }: FormattedRowData) => (
    <Box>{falsyCheckDate(created_at)}</Box>
  ),
  [SandboxTableHeadCellIds.IsReady]: ({ navigateToTeamProfile, is_ready }: FormattedRowData) => (
    <Box>{is_ready ? 'Ready' : 'Not Ready'}</Box>
  ),
  [SandboxTableHeadCellIds.Tags]: ({ navigateToTeamProfile, tags }: FormattedRowData) =>
    renderChips(tags || [], 2),
  [SandboxTableHeadCellIds.ArchivedAt]: ({
    navigateToTeamProfile,
    tags,
    archived_at,
  }: FormattedRowData) => <Box>{archived_at ? falsyCheckDate(archived_at) : ''}</Box>,
  [SandboxTableHeadCellIds.TeamName]: ({
    navigateToTeamProfile,
    tags,
    archived_at,
    team_name,
  }: FormattedRowData) => <Box>{team_name}</Box>,
  [SandboxTableHeadCellIds.Services]: ({ realm_services }: FormattedRowData) => {
    // De-duplicate services to ensure unique keys (Set preserves insertion order)
    const uniqueServices = Array.from(new Set(realm_services || []));
    return (
      <ArrayItemRendererWithCutOff<string>
        cutOffLimit={0}
        items={uniqueServices}
        renderItem={({ item }) => (
          <Chip label={item} size="small" color="primary" sx={{ mr: 0.5 }} />
        )}
        renderRemainingItems={({ remainingItems }) => (
          <Chip label={`+ ${remainingItems.length}`} size="small" sx={{ mr: 0.5 }} />
        )}
        getKey={(item) => item}
      />
    );
  },
  [SandboxTableHeadCellIds.Tenant]: ({ tenant }: FormattedRowData) => <Box>{tenant}</Box>,
  [SandboxTableHeadCellIds.Creator]: ({ navigateToTeamProfile, created_by }: FormattedRowData) => (
    <Box>{created_by}</Box>
  ),
  [SandboxTableHeadCellIds.Archiver]: ({
    navigateToTeamProfile,
    archived_by,
  }: FormattedRowData) => <Box>{archived_by}</Box>,
};

export const formatActiveRealms = ({ data }: { data: RealmListItem[] }) =>
  data.filter((realm) => !realm.archived_at);

export const formatNonActiveRealms = ({ data }: { data: RealmListItem[] }) =>
  data.filter((realm) => !!realm.archived_at);

export const realmIdCellProps = {
  id: SandboxTableHeadCellIds.RealmId,
  label: 'Realm Id',
};

export const checkboxCellProps = {
  id: SandboxTableHeadCellIds.Checkbox,
  label: '',
  disableSort: true,
};

export const teamNameCellProps = {
  id: SandboxTableHeadCellIds.TeamName,
  label: 'Team name',
};

export const statusCellProps = {
  id: SandboxTableHeadCellIds.Status,
  label: 'Status',
};

export const createdCellProps = {
  id: SandboxTableHeadCellIds.Created,
  label: 'Created',
};

export const creatorCellProps = {
  id: SandboxTableHeadCellIds.Creator,
  label: 'Creator',
};

export const tagsCellProps = {
  id: SandboxTableHeadCellIds.Tags,
  label: 'Tags',
  disableSort: true,
};

export const archiverCellProps = {
  id: SandboxTableHeadCellIds.Archiver,
  label: 'Archiver',
  disableSort: true,
};

export const archivedAtCellProps = {
  id: SandboxTableHeadCellIds.ArchivedAt,
  label: 'Archived At',
};

export const tenantCellProps = {
  id: SandboxTableHeadCellIds.Tenant,
  label: 'Tenant',
};
export const realmsCellProps = {
  id: SandboxTableHeadCellIds.Services,
  label: 'Services',
  disableSort: true,
};
